import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useAppStore from "src/stores/appStore";
import { selectedAssistantSelector } from "src/stores/selectors/appSelectors";
import {
  useFetchBenchmarksSetup,
  useFetchAllSetups,
  useFetchBenchmarkDefaults,
  useFetchBenchmarkOverrides,
  useUpsertBenchmarksSetup,
  useCreateBenchmarkOverride,
  useUpdateBenchmarkOverride,
  useDeleteBenchmarkOverride
} from "src/queries/hooks/benchmarks";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { ASSISTANT_CHANNEL_TO_BENCHMARK_CHANNEL, CHAT_CHANNEL } from "../AgentSettings.consts";
import { computeOverrideOps, getBenchmarkFormat, mergeOverrides, setOrDropEdit } from "./AnalyticsSettings.utils";
import useValueReferences from "./ValueReferences.hook";

const ID_FIELD = "metricId";
const VALUE_FIELD = "benchmark";
const DEFAULT_VALUE_FIELD = "defaultBenchmark";

const useAnalyticsSettings = (assistantId, setFeedbackConfig) => {
  const queryClient = useQueryClient();
  const selectedAssistant = useAppStore(selectedAssistantSelector);
  // An agent has exactly one channel, so the benchmark channel is derived from it rather
  // than chosen: a Web agent gets chat benchmarks, a Phone/Voice agent gets voice ones.
  const channel = ASSISTANT_CHANNEL_TO_BENCHMARK_CHANNEL[selectedAssistant?.channel];

  const [selectedSetup, setSelectedSetup] = useState(null);
  const [editedBenchmarks, setEditedBenchmarks] = useState({});
  const [pendingSetup, setPendingSetup] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);

  const {
    data: allSetups,
    isLoading: isSetupsLoading,
    isError: isSetupsError,
    error: setupsError
  } = useFetchAllSetups();

  const {
    data: setupData,
    isLoading: isSetupLoading,
    isError: isSetupError,
    error: setupError
  } = useFetchBenchmarksSetup(assistantId);

  const setup = setupData?.benchmarksSetupId;
  const activeSetup = selectedSetup || setup;

  const {
    data: allDefaults,
    isLoading: isDefaultsLoading,
    isError: isDefaultsError,
    error: defaultsError
  } = useFetchBenchmarkDefaults(channel, { enabled: !!activeSetup && !!channel });

  const {
    data: overrides,
    isLoading: isOverridesLoading,
    isError: isOverridesError,
    error: overridesError
  } = useFetchBenchmarkOverrides(assistantId, activeSetup, channel, { enabled: !!activeSetup && !!channel });

  const upsertSetupMutation = useUpsertBenchmarksSetup();
  const createOverrideMutation = useCreateBenchmarkOverride();
  const updateOverrideMutation = useUpdateBenchmarkOverride();
  const deleteOverrideMutation = useDeleteBenchmarkOverride();

  const {
    valueReferences,
    areSettingsChanged: areValueReferencesChanged,
    isLoading: isValueReferencesLoading,
    isPublishing: isValueReferencesPublishing,
    onValueReferenceUpdate,
    publish: publishValueReferences,
    clear: clearValueReferences
  } = useValueReferences(assistantId, setFeedbackConfig, { enabled: channel !== CHAT_CHANNEL });

  const currentSetupDefaults = allDefaults?.filter((b) => b.benchmarksSetupId === activeSetup && b.channel === channel) || [];

  const handleSetupChange = (newSetup) => {
    setPendingSetup(newSetup);
  };

  const confirmSetupChange = (keepCustomBenchmarks) => {
    setSelectedSetup(pendingSetup);
    if (!keepCustomBenchmarks) {
      setEditedBenchmarks({});
    }
    setPendingSetup(null);
  };

  const cancelSetupChange = () => {
    setPendingSetup(null);
  };

  const handleBenchmarkUpdate = (metricId, displayValue) => {
    const defaultBenchmark = currentSetupDefaults.find((d) => d.metricId === metricId);
    const override = overrides?.find((o) => o.metricId === metricId);
    const originalValue = override?.benchmark ?? defaultBenchmark?.benchmark;
    setOrDropEdit(setEditedBenchmarks, metricId, getBenchmarkFormat(metricId).fromDisplay(displayValue), originalValue);
  };

  const benchmarks = allDefaults && overrides
    ? mergeOverrides({
      defaults: currentSetupDefaults,
      overrides,
      edits: editedBenchmarks,
      idField: ID_FIELD,
      valueField: VALUE_FIELD,
      defaultValueField: DEFAULT_VALUE_FIELD
    })
    : undefined;

  const hasOverrides = overrides?.length > 0 || Object.keys(editedBenchmarks).length > 0;
  const areBenchmarksChanged =
    (!!selectedSetup && selectedSetup !== setup) || Object.keys(editedBenchmarks).length > 0;
  const areSettingsChanged = areBenchmarksChanged || areValueReferencesChanged;

  const isLoading =
    isSetupLoading || isDefaultsLoading || isOverridesLoading || isSetupsLoading || isValueReferencesLoading;
  const isError = isSetupError || isDefaultsError || isOverridesError || isSetupsError;
  const error = setupError || defaultsError || overridesError || setupsError;

  const clear = () => {
    setSelectedSetup(null);
    setEditedBenchmarks({});
    setPendingSetup(null);
    clearValueReferences();
  };

  const publishBenchmarks = async () => {
    try {
      if (selectedSetup && selectedSetup !== setup) {
        await upsertSetupMutation.mutateAsync({
          assistantId,
          data: { benchmarksSetupId: selectedSetup }
        });
      }

      const ops = computeOverrideOps({
        edits: editedBenchmarks,
        defaults: currentSetupDefaults,
        overrides,
        mutations: {
          create: createOverrideMutation,
          update: updateOverrideMutation,
          delete: deleteOverrideMutation
        },
        idField: ID_FIELD,
        valueField: VALUE_FIELD,
        basePayload: { assistantId, benchmarksSetupId: activeSetup, channel }
      });
      const results = await Promise.allSettled(ops);
      const hasFailures = results.some((r) => r.status === "rejected");

      await queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.BENCHMARKS_SETUP] });
      await queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.BENCHMARK_OVERRIDES] });

      if (hasFailures) {
        const firstError = results.find((r) => r.status === "rejected")?.reason;
        console.error("Error occurred while saving benchmark changes", firstError);
        setFeedbackConfig?.({
          show: true,
          message: "Some benchmark changes failed to save. Please try again.",
          severity: "error"
        });
        return false;
      }

      setEditedBenchmarks({});
      setSelectedSetup(null);
      return true;
    } catch (e) {
      console.error("Error occurred while saving analytics settings", e);
      setFeedbackConfig?.({
        show: true,
        message: "An error occurred while saving analytics settings. Please try again.",
        severity: "error"
      });
      return false;
    }
  };

  const publish = async () => {
    setIsPublishing(true);
    try {
      const tasks = [];
      if (areBenchmarksChanged) tasks.push(publishBenchmarks());
      if (areValueReferencesChanged) tasks.push(publishValueReferences());
      const results = await Promise.all(tasks);
      return results.every(Boolean);
    } finally {
      setIsPublishing(false);
    }
  };

  return {
    setup,
    activeSetup,
    channel,
    setSelectedSetup: handleSetupChange,
    areSettingsChanged,
    allSetups,
    benchmarks,
    hasOverrides,
    isLoading,
    isPublishing: isPublishing || isValueReferencesPublishing,
    isError,
    error,
    onBenchmarkUpdate: handleBenchmarkUpdate,
    pendingSetup,
    confirmSetupChange,
    cancelSetupChange,
    valueReferences,
    onValueReferenceUpdate,
    publish,
    clear
  };
};

export default useAnalyticsSettings;
